const GENERIC_MESSAGE =
  'The Pulse alert could not be saved. Try again or contact support.';

/**
 * The backend reports EDMS failures as technical strings such as
 * "Failed to create alert group: EDMS API Error (404): {...}". Those must
 * never reach a user verbatim (bug 165827), so anything recognizable as a
 * backend failure is translated into plain language here. Messages the
 * wizard throws for itself are already written for people and pass through.
 */
export function friendlyAlarmSaveError(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error ?? '');

  if (/failed to fetch|network ?error/i.test(raw)) {
    return 'Could not reach the server. Check your connection and try again.';
  }

  const status = matchEdmsStatus(raw);

  if (status === null) {
    // A resolver wrapper without a recognizable EDMS status carries nothing a
    // user can act on, so fall back to the generic message for those.
    return /^failed to (create|update|delete)/i.test(raw) || raw === ''
      ? GENERIC_MESSAGE
      : raw;
  }

  // EDMS environments without the alert-group endpoints answer a bare 404 on
  // group creation while single alerts keep working (bug 165827).
  if (status === 404 && /alert group/i.test(raw)) {
    return 'Alerts with multiple signals are not available in this environment yet. Save the alert with one signal, or try again after the next update.';
  }

  if (status === 400) {
    return 'Some alert settings were not accepted. Check the values and try again.';
  }
  if (status === 401 || status === 403) {
    return 'You do not have permission to save alerts. Contact support if you think you should.';
  }
  if (status === 404) {
    return 'Something this alert refers to no longer exists. Refresh the page and try again.';
  }
  if (status >= 500) {
    return 'The alert service is temporarily unavailable. Try again in a few minutes.';
  }
  return GENERIC_MESSAGE;
}

/**
 * The backend produces two shapes: "EDMS API ... (404 ...)" from the shared
 * client middleware and "EDMS API returned 404: ..." from the resolvers.
 */
function matchEdmsStatus(message: string): number | null {
  const match =
    message.match(/EDMS API[^(]*\((\d{3})/) ??
    message.match(/EDMS API returned (\d{3})/);
  return match ? parseInt(match[1], 10) : null;
}
